import React, { useCallback, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faYoutube } from "@nnr/fontawesome-classic-brands/icons/classic/brands";
import {
  faImage,
  faPlusCircle,
  faQuoteRight,
  faTable,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { type Editor } from "@tiptap/react";

// --- Icons ---
import { faChevronDown } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// --- Hooks ---
import { useLibraryFileUploadAction } from "@/action/use-library-file-upload-action";
import { useDocumentFileAction } from "@/action/use-document-file-action";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
import { useFileCollection } from "../../../hooks/use-file-collection";
import { toast } from "sonner";

interface ListDropdownAlignProps extends Omit<UnifiedButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The alignments to display in the dropdown.
   */
  onOpenChange?: (isOpen: boolean) => void;
  onVideoOpenChange?: (isOpen: boolean) => void;
}

function InsertComponentDropdownMenu({
  editor: providedEditor,
  onOpenChange,
  ...props
}: ListDropdownAlignProps) {
  const editor = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = useState(false);
  const { onSelectHandler } = useFileCollection({
    multiple: false,
    onSelect: (files) => {
      if (files.length > 0) {
        insertImage(files[0]);
      }
    },
  });

  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const documentUuid = useArtifactUuid("documentUuid");
  const { attachFile } = useDocumentFileAction();
  const { upload: uploadLibraryFile } = useLibraryFileUploadAction(
    groupUuid,
    kitUuid,
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const handleOpenChange = React.useCallback(
    (open: boolean, callback?: (isOpen: boolean) => void) => {
      setIsOpen(open);
      callback?.(open);
    },
    [],
  );

  const handleOnOpenChange = useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange],
  );

  async function insertImage(file: File) {
    if (!editor || !documentUuid || !kitUuid) return;

    setIsUploadingImage(true);
    try {
      const record = await uploadLibraryFile({ kitUuid, file });
      await attachFile(documentUuid, record.uuid);

      editor
        .chain()
        .focus()
        .setLibraryImage({
          src: record.uuid,
          alt: file.name,
        })
        .run();

      setIsOpen(false);
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Could not add image to document");
    } finally {
      setIsUploadingImage(false);
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <UnifiedButton
          type="button"
          data-style="ghost"
          data-active-state={"off"}
          role="button"
          tabIndex={-1}
          aria-label="Text alignment options"
          tooltip="Components"
          {...props}
        >
          <FontAwesomeIcon icon={faPlusCircle} />
          <span className="tiptap-button-text">Insert</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="tiptap-button-dropdown-small"
          />
        </UnifiedButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="rounded-full">
        <DropdownMenuGroup className="rounded-full">
          <UnifiedButton
            aria-label="Blockquote"
            tooltip="Blockquote"
            data-style="ghost"
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          >
            <FontAwesomeIcon icon={faQuoteRight} className="w-4" />
            <span className="tiptap-button-text">Blockquote</span>
          </UnifiedButton>

          <UnifiedButton
            aria-label="Insert Image"
            tooltip="Insert Image"
            data-style="ghost"
            disabled={isUploadingImage || !documentUuid}
            onClick={onSelectHandler}
          >
            <FontAwesomeIcon icon={faImage} className="w-4" />
            <span className="tiptap-button-text">
              {isUploadingImage ? "Uploading..." : "Image"}
            </span>
          </UnifiedButton>

          <UnifiedButton
            aria-label="Insert Video"
            tooltip="Insert Video"
            data-style="ghost"
            onClick={() =>
              editor?.chain().focus().setYouTubeVideo({ url: "" }).run()
            }
          >
            <FontAwesomeIcon icon={faYoutube} className="w-4" />
            <span className="tiptap-button-text">Video</span>
          </UnifiedButton>
          <UnifiedButton
            aria-label="Insert Table"
            tooltip="Insert Table"
            data-style="ghost"
            onClick={() =>
              editor
                ?.chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          >
            <FontAwesomeIcon icon={faTable} className="w-4" />
            <span className="tiptap-button-text">Table</span>
          </UnifiedButton>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default InsertComponentDropdownMenu;
